import { lazy } from 'react';

const RELOAD_FLAG = 'rsworks-chunk-reload';

/**
 * A navigation that starts less than this long after a recovery reload is the
 * recovery navigation itself; anything later — a tab that breaks again across
 * a *later* deploy — may reload once more. Compared against navigation start,
 * never against rejection time (see isRecoveryNavigation).
 */
const RELOAD_COOLDOWN_MS = 30_000;

/** One reload per page load, however many importers fail at once. */
let reloadRequested = false;

function readReloadedAt() {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return null;
    const parsed = Number(raw);
    // Pre-RSW-618 tabs stored '1'. It parses to an ancient timestamp, so it
    // reads as expired and allows one reload — the old one-shot behaviour.
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // privacy mode — fall through; worst case the import error surfaces
  }
}

/** True when a recovery reload ran less than the cooldown before this navigation. */
function reloadedRecently() {
  const reloadedAt = readReloadedAt();
  if (reloadedAt === null) return false;
  return Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;
}

/**
 * Whether THIS document is the recovery navigation — decided once, at module
 * evaluation. It must not be re-derived when an import rejects: a chunk request
 * on the recovery page can take longer than the cooldown to reject (slow CDN
 * timeout, tab suspended mid-import), and a rejection-time check would see an
 * expired guard and reload the same broken chunk forever.
 */
const isRecoveryNavigation = typeof window !== 'undefined' && reloadedRecently();

/**
 * React.lazy with stale-deploy recovery (RSW-609).
 *
 * A tab left open across a release holds the old entry bundle, whose lazy
 * imports point at the previous release's hashed chunks. nginx returns 404
 * for missing assets, so the import rejects and the error boundary would
 * interrupt login until a manual reload. Instead: reload once to pick up the
 * fresh index. If the import still fails after that reload, the error is real
 * — it surfaces to the error boundary as before.
 *
 * The guard is a timestamp rather than a "did we reload" boolean, and success
 * never clears it (RSW-618). Every lazy route shares this one sessionStorage
 * key, so clearing on success let a chunk that loaded fine — Auth0AppProvider,
 * say — wipe the guard a genuinely broken chunk had just set. The next failure
 * then read an empty guard and reloaded again, turning a single bad chunk into
 * an unbounded reload loop that never reached the error boundary. Letting the
 * timestamp expire on its own keeps the guard immune to that interleaving while
 * still allowing a later deploy to self-heal.
 *
 * Expiry classifies the *navigation*, never the rejection: the recovery page
 * refuses to reload no matter how late its imports reject.
 */
export default function lazyWithReload(importer) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      if (typeof window !== 'undefined' && !reloadRequested && !isRecoveryNavigation) {
        reloadRequested = true;
        markReloaded();
        window.location.reload();
        // Keep the Suspense fallback up while the reload lands.
        return new Promise(() => {});
      }
      throw err;
    }
  });
}
